import {
    MatChip,
    MatChipGrid,
    MatChipInput,
    MatChipInputEvent,
    MatChipRemove,
    MatChipRow
} from '@angular/material/chips';
import {Component, inject, Input, OnInit, ViewChild} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import {MatIcon} from '@angular/material/icon';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {MatSlideToggle} from '@angular/material/slide-toggle';

import {MatInput} from '@angular/material/input';
import {NotificationSettings} from '../../../models/notification-settings.model';
import {COMMA, ENTER} from '@angular/cdk/keycodes';

const dnsLabelPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

export const webhookUrlValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const rawValue = String(control.value ?? '');
    const candidate = rawValue.trim();
    if (candidate.length === 0) {
        return null;
    }

    const hasControlCharacter = Array.from(rawValue)
        .some(character => character.charCodeAt(0) <= 31 || character.charCodeAt(0) === 127);
    if (!/^https:\/\//i.test(candidate) ||
        hasControlCharacter ||
        rawValue.includes('\\') ||
        candidate.includes('#')) {
        return {webhookUrl: true};
    }

    let url: URL;
    try {
        url = new URL(candidate);
    } catch {
        return {webhookUrl: true};
    }

    const authority = candidate.slice(candidate.indexOf('://') + 3).split(/[/?#]/, 1)[0];
    const host = url.hostname;
    const labels = host.split('.');
    const isIpLiteral = host.startsWith('[') || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(host);
    const hasValidHost = host.length <= 253 &&
        !host.endsWith('.') &&
        labels.length >= 2 &&
        labels.every(label => dnsLabelPattern.test(label));

    return url.protocol === 'https:' &&
        !authority.includes('@') &&
        url.username.length === 0 &&
        url.password.length === 0 &&
        !isIpLiteral &&
        hasValidHost
        ? null
        : {webhookUrl: true};
};

@Component({
    selector: 'app-notification-settings',
    templateUrl: './notification-settings.component.html',
    imports: [
        MatIcon,
        MatFormField,
        MatSlideToggle,
        FormsModule,
        MatChipGrid,
        MatChipInput,
        MatChipRemove,
        MatChipRow,
        MatLabel,
        MatError,
        ReactiveFormsModule,
        MatInput
    ],
    styleUrls: ['./notification-settings.component.scss']
})
export class NotificationSettingsComponent implements OnInit {
    private fb = inject(FormBuilder);

    @Input() notificationForm: FormGroup;
    @Input() notificationSettings: NotificationSettings;

    separatorKeysCodes: number[] = [ENTER, COMMA];
    // Optional: if you need direct access to the chip list
    @ViewChild('chipList') chipList!: MatChip;

    ngOnInit(): void {
        // Initialize your form with default values
        this.notificationForm.addControl('emailNotificationsEnabled', this.fb.control(this.notificationSettings.emailNotificationsEnabled));
        this.notificationForm.addControl('emailNotificationRecipients', this.fb.control(this.notificationSettings.emailNotificationRecipients));
        this.notificationForm.addControl('emailNotificationSubject', this.fb.control(this.notificationSettings.emailNotificationSubject));
        this.notificationForm.addControl('webhookNotificationsEnabled', this.fb.control(this.notificationSettings.webhookNotificationsEnabled));
        this.notificationForm.addControl('webhookNotificationUrl', this.fb.control(this.notificationSettings.webhookNotificationUrl, webhookUrlValidator));
    }

    addRecipient(event: MatChipInputEvent): void {
        const input = event.input;
        const value = event.value;
        if ((value || '').trim()) {
            const currentRecipients = this.notificationForm.get('emailNotificationRecipients')?.value || [];
            currentRecipients.push(value.trim());
            this.notificationForm.get('emailNotificationRecipients')?.setValue(currentRecipients);
        }
        // Clear the input value
        if (input) {
            input.value = '';
        }
    }

    removeRecipient(recipient: string): void {
        const recipients = this.notificationForm.get('emailNotificationRecipients')?.value || [];
        const index = recipients.indexOf(recipient);
        if (index >= 0) {
            recipients.splice(index, 1);
            this.notificationForm.get('emailNotificationRecipients')?.setValue(recipients);
        }
    }
}
